import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IUnit } from 'interfaces/IInput';

/**
 * Unit conversion utilities based on the GUnit / GParameter system.
 *
 * **Primary path** — globalId is present and resolves to a GUnit record:
 *   result = (value / source.multiplier) * target.multiplier
 *
 * **Fallback path** — globalId is null, empty, or not found in gUnits:
 *   The unit string (e.g. "kg", "Tonnes", "ha") is normalised and matched
 *   against known aliases to derive a conversion factor.
 *   This covers legacy data, hand-entered units, and edge cases where the
 *   GUnit catalogue is incomplete.
 */

/* ═══════════════════════════════════════════════════════════════
   String-based unit recognition (fallback layer)
   ═══════════════════════════════════════════════════════════════ */

/**
 * Lowercase, strip spaces / dots / hyphens / underscores.
 * "Metric Tonnes" → "metrictonnes", "kg/ha" → "kg/ha"
 */
const normalise = (unit: string): string =>
  unit
    .trim()
    .toLowerCase()
    .replace(/[\s.\-_]/g, '');

/* ── weight recognisers ──────────────────────────────────────── */

const TONNE_ALIASES = new Set([
  't',
  'ton',
  'tons',
  'tonne',
  'tonnes',
  'metricton',
  'metrictons',
  'metrictonne',
  'metrictonnes',
  'mt',
]);

const KG_ALIASES = new Set([
  'kg',
  'kgs',
  'kilogram',
  'kilograms',
  'kilogramme',
  'kilogrammes',
]);

const GRAM_ALIASES = new Set([
  'g',
  'gm',
  'gms',
  'gram',
  'grams',
  'gramme',
  'grammes',
]);

const MG_ALIASES = new Set([
  'mg',
  'mgs',
  'milligram',
  'milligrams',
  'milligramme',
  'milligrammes',
]);

/* ── liquid recognisers ──────────────────────────────────────── */

const LITRE_ALIASES = new Set([
  'l',
  'lt',
  'ltr',
  'ltrs',
  'litre',
  'litres',
  'liter',
  'liters',
]);

const ML_ALIASES = new Set([
  'ml',
  'mls',
  'millilitre',
  'millilitres',
  'milliliter',
  'milliliters',
]);

const MEGALITRE_ALIASES = new Set([
  'ml(mega)',
  'megalitre',
  'megalitres',
  'megaliter',
  'megaliters',
]);

/* ── area recognisers ────────────────────────────────────────── */

const HA_ALIASES = new Set([
  'ha',
  'has',
  'hectare',
  'hectares',
]);

const SQM_ALIASES = new Set([
  'm²',
  'm2',
  'sqm',
  'squaremetre',
  'squaremetres',
  'squaremeter',
  'squaremeters',
]);

const SQKM_ALIASES = new Set([
  'km²',
  'km2',
  'sqkm',
  'squarekilometre',
  'squarekilometres',
  'squarekilometer',
  'squarekilometers',
]);

const SQCM_ALIASES = new Set([
  'cm²',
  'cm2',
  'sqcm',
  'squarecentimetre',
  'squarecentimetres',
  'squarecentimeter',
  'squarecentimeters',
]);

const ACRE_ALIASES = new Set([
  'ac',
  'acre',
  'acres',
]);

/* ── density (bulk density) aliases → factor to kg/L ─────────── */

/**
 * Lookup: normalised unit string → conversion factor to kg/L.
 * Covers the most common bulk-density units used in agriculture.
 *
 * Derivation:
 *   g/mL  = 1 g per mL  = 1000 g/L  = 1 kg/L      → 1
 *   g/L   = 0.001 kg/L                             → 0.001
 *   mg/mL = 1 g/L                                  → 0.001
 *   kg/m³ = 1 kg / 1000 L                          → 0.001
 *   t/m³  = 1000 kg / 1000 L = 1 kg/L             → 1
 *   kg/dm³= 1 kg/L (dm³ ≡ L)                      → 1
 */
const DENSITY_TO_KG_PER_L: Record<string, number> = {
  'kg/l': 1,
  'g/ml': 1,
  'g/ml(mega)': 1,   // unlikely but guard the alias table
  't/m3': 1,
  't/m³': 1,
  'kg/dm3': 1,
  'kg/dm³': 1,
  'g/l': 0.001,
  'mg/ml': 0.001,
  'kg/m3': 0.001,
  'kg/m³': 0.001,
};

/* ── enum for recognised unit kind ───────────────────────────── */

type WeightKind = 'mg' | 'g' | 'kg' | 't';
type LiquidKind = 'mL' | 'L' | 'ML';
type AreaKind = 'cm²' | 'm²' | 'ha' | 'km²' | 'ac';

const identifyWeight = (n: string): WeightKind | null => {
  if (TONNE_ALIASES.has(n)) return 't';
  if (KG_ALIASES.has(n)) return 'kg';
  if (GRAM_ALIASES.has(n)) return 'g';
  if (MG_ALIASES.has(n)) return 'mg';
  return null;
};

const identifyLiquid = (n: string): LiquidKind | null => {
  if (MEGALITRE_ALIASES.has(n)) return 'ML';
  if (ML_ALIASES.has(n)) return 'mL';
  if (LITRE_ALIASES.has(n)) return 'L';
  return null;
};

const identifyArea = (n: string): AreaKind | null => {
  if (HA_ALIASES.has(n)) return 'ha';
  if (SQM_ALIASES.has(n)) return 'm²';
  if (SQKM_ALIASES.has(n)) return 'km²';
  if (SQCM_ALIASES.has(n)) return 'cm²';
  if (ACRE_ALIASES.has(n)) return 'ac';
  return null;
};

/* ── conversion factor tables (to target unit) ───────────────── */

/** Factors: 1 <source> = factor <tonnes> */
const WEIGHT_TO_TONNES: Record<WeightKind, number> = {
  mg: 1e-9,
  g: 1e-6,
  kg: 0.001,
  t: 1,
};

/** Factors: 1 <source> = factor <litres> */
const LIQUID_TO_LITRES: Record<LiquidKind, number> = {
  mL: 0.001,
  L: 1,
  ML: 1e6,
};

/** Factors: 1 <source> = factor <hectares> */
const AREA_TO_HA: Record<AreaKind, number> = {
  'cm²': 1e-8,
  'm²': 1e-4,
  ha: 1,
  'km²': 100,
  ac: 0.404686,
};

/* ── string-based fallback converters ────────────────────────── */

/**
 * Best-effort conversion to tonnes using only the unit string.
 * Returns `null` if the unit cannot be recognised.
 */
const fallbackToTonnes = (
  value: number,
  unitStr: string,
  bulkDensity?: number | null
): number | null => {
  const n = normalise(unitStr);

  const wk = identifyWeight(n);
  if (wk) return value * WEIGHT_TO_TONNES[wk];

  const lk = identifyLiquid(n);
  if (lk) {
    const litres = value * LIQUID_TO_LITRES[lk];
    const density =
      bulkDensity !== null && bulkDensity !== undefined && bulkDensity > 0
        ? bulkDensity
        : 1;
    return (litres * density) / 1000;
  }

  return null;
};

/**
 * Best-effort conversion to hectares using only the unit string.
 * Returns `null` if the unit cannot be recognised.
 */
const fallbackToHectares = (
  value: number,
  unitStr: string
): number | null => {
  const n = normalise(unitStr);
  const ak = identifyArea(n);
  if (ak) return value * AREA_TO_HA[ak];
  return null;
};

/* ═══════════════════════════════════════════════════════════════
   GUnit-based helpers (primary path)
   ═══════════════════════════════════════════════════════════════ */

const findByGlobalId = (
  gUnits: GUnit[],
  globalId: string
): GUnit | undefined => gUnits.find((u) => u.globalId === globalId);

const findByParamAndCode = (
  gUnits: GUnit[],
  parameterId: string,
  code: string
): GUnit | undefined =>
  gUnits.find((u) => u.parameterId === parameterId && u.code === code);

const findBaseUnit = (
  gUnits: GUnit[],
  parameterId: string
): GUnit | undefined =>
  gUnits.find((u) => u.parameterId === parameterId && u.isBaseUnit);

const isValidGlobalId = (id: string | null | undefined): id is string =>
  typeof id === 'string' && id.length > 0;

/* ═══════════════════════════════════════════════════════════════
   Public API
   ═══════════════════════════════════════════════════════════════ */

/**
 * Convert a value between two units in the same GParameter family.
 * Returns `null` when either unit is not found or they belong to
 * different parameter families.
 */
export const convertUnit = (
  value: number,
  sourceGlobalId: string,
  targetGlobalId: string,
  gUnits: GUnit[]
): number | null => {
  const source = findByGlobalId(gUnits, sourceGlobalId);
  const target = findByGlobalId(gUnits, targetGlobalId);
  if (!source || !target) return null;
  if (source.parameterId !== target.parameterId) return null;
  return (value / source.multiplier) * target.multiplier;
};

export type UnitKind = 'mass' | 'liquid';

/**
 * Identify whether a unit belongs to the mass or liquid family.
 *
 * Primary: resolve via GUnit globalId, then check the family's base unit
 * code ('kg' or 'L' by data-model convention).
 * Fallback: match the unit string against the weight/liquid alias tables.
 * Returns `null` when the unit doesn't fall into either recognised kind
 * (e.g. a count or area unit).
 */
export const resolveUnitKind = (
  unit: IUnit | null | undefined,
  gUnits: GUnit[]
): UnitKind | null => {
  if (!unit) return null;

  if (isValidGlobalId(unit.globalId)) {
    const source = findByGlobalId(gUnits, unit.globalId);
    if (source) {
      const base = findBaseUnit(gUnits, source.parameterId);
      if (base?.code === 'kg') return 'mass';
      if (base?.code === 'L') return 'liquid';
    }
  }

  if (unit.unit) {
    const n = normalise(unit.unit);
    if (identifyWeight(n)) return 'mass';
    if (identifyLiquid(n)) return 'liquid';
  }

  return null;
};

/**
 * Convert a mass-kind value to kilograms. Unlike `convertToTonnes`, this
 * never blends in a liquid value via bulk density — callers should check
 * `resolveUnitKind` is `'mass'` first. Returns `null` when the unit isn't a
 * recognised mass unit.
 */
export const convertToKg = (
  value: number,
  sourceUnit: IUnit | null | undefined,
  gUnits: GUnit[]
): number | null => {
  if (!sourceUnit) return null;

  if (isValidGlobalId(sourceUnit.globalId)) {
    const source = findByGlobalId(gUnits, sourceUnit.globalId);
    if (source) {
      const base = findBaseUnit(gUnits, source.parameterId);
      if (base?.code === 'kg') {
        return source.isBaseUnit ? value : value / source.multiplier;
      }
    }
  }

  if (sourceUnit.unit) {
    const wk = identifyWeight(normalise(sourceUnit.unit));
    if (wk) return value * WEIGHT_TO_TONNES[wk] * 1000;
  }

  return null;
};

/**
 * Convert a liquid-kind value to litres. Unlike `convertToTonnes`, this
 * never blends into a mass value via bulk density — callers should check
 * `resolveUnitKind` is `'liquid'` first. Returns `null` when the unit isn't
 * a recognised liquid unit.
 */
export const convertToLitres = (
  value: number,
  sourceUnit: IUnit | null | undefined,
  gUnits: GUnit[]
): number | null => {
  if (!sourceUnit) return null;

  if (isValidGlobalId(sourceUnit.globalId)) {
    const source = findByGlobalId(gUnits, sourceUnit.globalId);
    if (source) {
      const base = findBaseUnit(gUnits, source.parameterId);
      if (base?.code === 'L') {
        return source.isBaseUnit ? value : value / source.multiplier;
      }
    }
  }

  if (sourceUnit.unit) {
    const lk = identifyLiquid(normalise(sourceUnit.unit));
    if (lk) return value * LIQUID_TO_LITRES[lk];
  }

  return null;
};

/**
 * Convert a value to its parameter family's base unit.
 */
export const convertToBaseUnit = (
  value: number,
  sourceGlobalId: string,
  gUnits: GUnit[]
): number | null => {
  const source = findByGlobalId(gUnits, sourceGlobalId);
  if (!source) return null;
  if (source.isBaseUnit) return value;
  return value / source.multiplier;
};

/**
 * Convert an area value to hectares.
 *
 * Primary: resolve via GUnit globalId + multiplier.
 * Fallback: if globalId is missing or not found, match the unit string
 * against known area aliases and apply a fixed conversion factor.
 */
export const convertToHectares = (
  value: number,
  sourceUnit: IUnit | null | undefined,
  gUnits: GUnit[]
): number | null => {
  if (!sourceUnit) return null;

  // Primary path — GUnit lookup
  if (isValidGlobalId(sourceUnit.globalId)) {
    const source = findByGlobalId(gUnits, sourceUnit.globalId);
    if (source) {
      const ha = findByParamAndCode(gUnits, source.parameterId, 'ha');
      if (ha) {
        return (value / source.multiplier) * ha.multiplier;
      }
    }
  }

  // Fallback — string-based
  if (sourceUnit.unit) {
    return fallbackToHectares(value, sourceUnit.unit);
  }

  return null;
};

/**
 * Convert a quantity value to tonnes.
 *
 * Primary: resolve via GUnit globalId + multiplier.
 * Fallback: if globalId is missing or not found, match the unit string
 * against known weight / liquid aliases and apply a fixed factor.
 *
 * Liquid conversion uses bulkDensity when available; falls back to
 * 1 L = 1 kg (BPAM Pattern 2) when it is 0 / null / undefined.
 */
export const convertToTonnes = (
  value: number,
  sourceUnit: IUnit | null | undefined,
  gUnits: GUnit[],
  bulkDensity?: number | null
): number | null => {
  if (!sourceUnit) return null;

  // Primary path — GUnit lookup
  if (isValidGlobalId(sourceUnit.globalId)) {
    const source = findByGlobalId(gUnits, sourceUnit.globalId);
    if (source) {
      // Convert to base unit (kg or L depending on family)
      const baseValue = source.isBaseUnit
        ? value
        : value / source.multiplier;

      // Try direct conversion if family has Tonnes
      const tonnesInFamily = findByParamAndCode(
        gUnits,
        source.parameterId,
        't'
      );
      if (tonnesInFamily) {
        return baseValue * tonnesInFamily.multiplier;
      }

      // Fallback by base-unit code
      const base = findBaseUnit(gUnits, source.parameterId);
      if (base) {
        if (base.code === 'kg') {
          return baseValue / 1000;
        }
        if (base.code === 'L') {
          const density =
            bulkDensity !== null &&
            bulkDensity !== undefined &&
            bulkDensity > 0
              ? bulkDensity
              : 1;
          return (baseValue * density) / 1000;
        }
      }
    }
  }

  // Fallback — string-based
  if (sourceUnit.unit) {
    return fallbackToTonnes(value, sourceUnit.unit, bulkDensity);
  }

  return null;
};

export const isMillilitreUnit = (unit: string | null | undefined): boolean =>
  !!unit && identifyLiquid(normalise(unit)) === 'mL';

export const convertMillilitresToLitres = (value: number): number =>
  value * LIQUID_TO_LITRES.mL;

/**
 * Convert a bulk-density value to kg/L.
 *
 * Primary path — GUnit globalId lookup:
 *   Normalises the value to the parameter family's base unit, then applies
 *   the density factor for that base unit's code (if it appears in
 *   DENSITY_TO_KG_PER_L). This covers GUnit-tracked density parameters
 *   where the base unit is something like "kg/L" or "g/mL".
 *
 * Fallback — string matching against DENSITY_TO_KG_PER_L aliases.
 *
 * Returns `null` when the unit is absent or unrecognised; the caller
 * should then treat the raw value as already in kg/L.
 */
export const convertToBulkDensityKgPerL = (
  value: number,
  sourceUnit: IUnit | null | undefined,
  gUnits: GUnit[]
): number | null => {
  if (!sourceUnit) return null;

  // Primary — GUnit lookup
  if (isValidGlobalId(sourceUnit.globalId)) {
    const source = findByGlobalId(gUnits, sourceUnit.globalId);
    if (source) {
      const baseValue = source.isBaseUnit ? value : value / source.multiplier;
      const base = findBaseUnit(gUnits, source.parameterId);
      if (base?.code) {
        const factor = DENSITY_TO_KG_PER_L[normalise(base.code)];
        if (factor !== undefined) return baseValue * factor;
      }
    }
  }

  // Fallback — string-based
  if (sourceUnit.unit) {
    const factor = DENSITY_TO_KG_PER_L[normalise(sourceUnit.unit)];
    if (factor !== undefined) return value * factor;
  }

  return null;
};
